import React from 'react';
import dayjs from 'dayjs';
import { dateTimeFormat } from '../../utilities/constants';
import { type SelectOption } from '../controls/InputSelect';
import TimelineItem from './TimelineItem';

interface EventTimelineProps {
  buildDateTime: string;
  startDateTime: string;
  endDateTime: string;
  deleteDateTime: string;
  timezone: SelectOption;
  onBuildDateTimeChange: (value: string) => void;
  onStartDateTimeChange: (value: string) => void;
  onEndDateTimeChange: (value: string) => void;
  onDeleteDateTimeChange: (value: string) => void;
  buildDateError?: string;
  startDateError?: string;
  endDateError?: string;
  deleteDateError?: string;
  editable?: boolean;
}

interface TimelineDescriptionProps {
  description?: string;
}

const TimelineDescription: React.FC<TimelineDescriptionProps> = ({ description = undefined }) => {
  return (
    <div className="text-xs py-1">
      {/* build time desc */}
      <span>{description ?? ''}</span>
    </div>
  );
};

const EventTimeline: React.FC<EventTimelineProps> = ({
  buildDateTime,
  startDateTime,
  endDateTime,
  deleteDateTime,
  timezone,
  onBuildDateTimeChange,
  onStartDateTimeChange,
  onEndDateTimeChange,
  onDeleteDateTimeChange,
  buildDateError,
  startDateError,
  endDateError,
  deleteDateError,
  editable = false,
}) => {
  // Convert datetime-local format to display format
  const formatDisplayTime = (dateTimeValue: string) => {
    if (!dateTimeValue) return '';

    // Create a date object from the datetime-local value
    const date = new Date(dateTimeValue);

    // Format for display in the specified timezone
    return dayjs(date).tz(timezone.id).format(dateTimeFormat);
  };

  // Convert datetime-local format to UTC ISO string for timezone display
  const formatTimezoneDisplay = (dateTimeValue: string) => {
    if (!dateTimeValue) return '';

    const date = new Date(dateTimeValue);
    return date.toISOString();
  };

  // Calculate and format time delta between two dates
  const formatTimeDelta = (startDate: string, endDate: string) => {
    if (!startDate || !endDate) return '';

    const start = dayjs(startDate);
    const end = dayjs(endDate);
    const diffInMinutes = end.diff(start, 'minute');

    if (diffInMinutes < 60) {
      return `${diffInMinutes}m`;
    } else if (diffInMinutes < 1440) {
      // Less than 24 hours
      const hours = Math.floor(diffInMinutes / 60);
      const minutes = diffInMinutes % 60;
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      const hours = Math.floor((diffInMinutes % 1440) / 60);
      return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
    }
  };

  return (
    <div className="bg-dataops-yale-blue/5 p-2 rounded-md mb-4">
      <h4 className="text-sm text-gray-500 mb-2">{`Timezone: ${timezone.name}`}</h4>
      <div className="relative">
        <div className="flex space-x-6">
          <div className="flex-0">
            <TimelineItem
              id="buildDateTime"
              name="buildDateTime"
              label="Build Time"
              value={buildDateTime}
              onChange={onBuildDateTimeChange}
              error={buildDateError}
              editable={editable}
              showDelta={true}
              deltaValue={formatTimeDelta(buildDateTime, startDateTime)}
              colors={{
                indicator: 'bg-dataops-yale-blue',
                border: 'border-2 border-[#F3F4F6]',
                text: 'text-dataops-yale-blue',
                line: 'bg-dataops-yale-blue',
              }}
              formatDisplayTime={formatDisplayTime}
              formatTimezoneDisplay={formatTimezoneDisplay}
            />

            <TimelineItem
              id="startDateTime"
              name="startDateTime"
              label="Start Time"
              value={startDateTime}
              onChange={onStartDateTimeChange}
              error={startDateError}
              editable={editable}
              minValue={buildDateTime}
              showDelta={true}
              deltaValue={formatTimeDelta(startDateTime, endDateTime)}
              colors={{
                indicator: 'bg-dataops-yale-blue',
                border: 'border-2 border-[#F3F4F6]',
                text: 'text-dataops-yale-blue',
                line: 'bg-dataops-yale-blue',
              }}
              formatDisplayTime={formatDisplayTime}
              formatTimezoneDisplay={formatTimezoneDisplay}
            />

            <TimelineItem
              id="endDateTime"
              name="endDateTime"
              label="End Time"
              value={endDateTime}
              onChange={onEndDateTimeChange}
              error={endDateError}
              editable={editable}
              minValue={startDateTime}
              showDelta={true}
              deltaValue={formatTimeDelta(endDateTime, deleteDateTime)}
              colors={{
                indicator: 'bg-dataops-yale-blue',
                border: 'border-2 border-[#F3F4F6]',
                text: 'text-dataops-yale-blue',
                line: 'bg-dataops-yale-blue',
              }}
              formatDisplayTime={formatDisplayTime}
              formatTimezoneDisplay={formatTimezoneDisplay}
            />

            <TimelineItem
              id="deleteDateTime"
              name="deleteDateTime"
              label="Decommission Time"
              value={deleteDateTime}
              onChange={onDeleteDateTimeChange}
              error={deleteDateError}
              editable={editable}
              minValue={endDateTime}
              isLast={true}
              colors={{
                indicator: 'bg-dataops-yale-blue',
                border: 'border-2 border-[#F3F4F6]',
                text: 'text-dataops-yale-blue',
              }}
              formatDisplayTime={formatDisplayTime}
              formatTimezoneDisplay={formatTimezoneDisplay}
            />
          </div>
          <div className="flex-1 divide-y divide-dataops-taupe-gray/50">
            <TimelineDescription description="Build time is when we will create the Snowflake accounts and configure them using project pipelines." />
            <TimelineDescription description="Start time is when the event will start and attendees will begin the lab. Used for Milestones." />
            <TimelineDescription description="End time is when the event will end and attendees can no longer join. Existing attendees keep access." />
            <TimelineDescription description="Decommission time is when all Snowflake accounts are dropped. This is permanent and cannot be undone." />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventTimeline;
